import { useState } from "react";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import { FiMail, FiLock, FiCheckCircle, FiMapPin } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import OAuth from "../components/OAuth";
import { signInWithEmailAndPassword, getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import Photo from "../assets/Pictures/linaup.png";
import { useTranslation } from "react-i18next";

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const { email, password } = formData;
  const navigate = useNavigate();
  const { t } = useTranslation();

  function onChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();

    try {
      setLoading(true);

      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      if (userCredential.user) {
        navigate("/");
      }
    } catch (error) {
      toast.error(t("incorrect_email_password"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="bg-[#f6f8f7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">

        <div className="relative min-h-[650px] overflow-hidden rounded-[28px] shadow-xl">

          {/* IMAGE */}
          <img
            src={Photo}
            alt="Lina Immo"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* OVERLAY */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-black/10" />

          <div className="relative z-10 grid lg:grid-cols-2 min-h-[650px]">

            {/* LEFT SIDE */}
            <div className="hidden lg:flex flex-col justify-center px-14 xl:px-20">

              <span className="inline-flex w-fit items-center rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-800 mb-5">
                LINA IMMO
              </span>

              <h1 className="text-5xl font-extrabold leading-tight text-slate-900">
                Bienvenue sur
                <span className="block text-green-700">
                  Lina Immo
                </span>
              </h1>

              <p className="mt-6 max-w-md text-lg leading-8 text-slate-600">
                Connectez-vous pour retrouver vos annonces, vos favoris,
                vos recherches et toutes vos opportunités immobilières.
              </p>

              <div className="mt-9 space-y-4">

                <Feature
                  icon={<FiCheckCircle />}
                  title="Des annonces sélectionnées"
                />

                <Feature
                  icon={<FiLock />}
                  title="Un espace personnel sécurisé"
                />

                <Feature
                  icon={<FiMapPin />}
                  title="Vos opportunités immobilières en un seul endroit"
                />

              </div>

              <p className="mt-12 text-xl italic font-medium text-green-800">
                L'immobilier, simplement.
              </p>

            </div>

            {/* FORM */}
            <div className="flex items-center justify-center px-4 py-10 sm:px-8">

              <div className="w-full max-w-md rounded-[26px] bg-white/95 backdrop-blur-md p-7 sm:p-9 shadow-2xl border border-white">

                <div className="mb-8">

                  <div className="flex items-center justify-between">

                    <div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-green-700">
                        Lina Immo
                      </p>

                      <h2 className="mt-1 text-3xl font-bold text-slate-900">
                        {t("sign_in")}
                      </h2>
                    </div>

                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-2xl text-green-700">
                      🏠
                    </div>

                  </div>

                  <p className="mt-3 text-sm text-slate-500">
                    Heureux de vous revoir.
                  </p>

                </div>

                <form onSubmit={onSubmit}>

                  {/* EMAIL */}
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    Email
                  </label>

                  <div className="relative mb-5">

                    <FiMail className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      required
                      type="email"
                      id="email"
                      value={email}
                      onChange={onChange}
                      placeholder={t("email_placeholder")}
                      autoComplete="email"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 ps-11 pe-4 text-slate-800 outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
                    />

                  </div>

                  {/* PASSWORD */}
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    {t("password_placeholder")}
                  </label>

                  <div className="relative mb-3">

                    <FiLock className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      required
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={onChange}
                      placeholder={t("password_placeholder")}
                      autoComplete="current-password"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 ps-11 pe-12 text-slate-800 outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
                    />

                    <button
                      type="button"
                      aria-label={
                        showPassword
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute end-4 top-1/2 -translate-y-1/2 text-xl text-slate-500 hover:text-green-700"
                    >
                      {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                    </button>

                  </div>

                  <div className="flex justify-end mb-6">
                    <Link
                      to="/ForgotPassword"
                      className="text-sm font-semibold text-green-700 hover:text-green-800"
                    >
                      {t("forgot_password")}
                    </Link>
                  </div>

                  {/* SUBMIT */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-green-700 px-6 py-3.5 font-semibold text-white shadow-lg shadow-green-700/20 transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? "Connexion..." : t("sign_in")}
                  </button>

                  {/* OR */}
                  <div className="flex items-center my-6 before:flex-1 before:border-t before:border-slate-200 after:flex-1 after:border-t after:border-slate-200">
                    <span className="mx-4 text-xs font-semibold uppercase text-slate-400">
                      {t("or")}
                    </span>
                  </div>

                  <OAuth />

                  <p className="mt-7 text-center text-sm text-slate-600">
                    {t("no_account")}{" "}
                    <Link
                      to="/signup"
                      className="font-bold text-green-700 hover:text-green-800"
                    >
                      {t("sign_up")}
                    </Link>
                  </p>

                </form>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon, title }) {
  return (
    <div className="flex items-center gap-3 text-slate-700">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-lg text-green-700">
        {icon}
      </div>

      <span className="font-medium">{title}</span>
    </div>
  );
}